export type Ga4EventName =
	| "loan_application_started"
	| "co_applicant_added"
	| "loan_application_submitted";

export type Ga4EventParameters = {
	market?: "se" | "fi";
	product_type?: "loan" | "credit_card" | "sales_finance";
	step_name?: string;
	step_number?: number;
};

type Gtag = (...args: unknown[]) => void;

declare global {
	interface Window {
		dataLayer?: unknown[];
		gtag?: Gtag;
	}
}

const MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]+$/;
const SCRIPT_SELECTOR = "script[data-leabank-ga4]";

let analyticsAllowed = false;
let configuredMeasurementId: string | undefined;

export const isGa4MeasurementId = (value: string): boolean =>
	MEASUREMENT_ID_PATTERN.test(value);

const loadGa4 = (measurementId: string) => {
	if (
		typeof window === "undefined" ||
		configuredMeasurementId === measurementId
	) {
		return;
	}

	window.dataLayer ??= [];
	window.gtag = function () {
		// gtag.js interprets Arguments objects as commands, not plain arrays.
		// eslint-disable-next-line prefer-rest-params
		window.dataLayer?.push(arguments);
	};
	window.gtag("js", new Date());
	window.gtag("config", measurementId, {
		send_page_view: false,
		allow_google_signals: false,
		allow_ad_personalization_signals: false,
	});

	if (!document.querySelector(SCRIPT_SELECTOR)) {
		const script = document.createElement("script");
		script.async = true;
		script.dataset.leabankGa4 = "true";
		script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
		document.head.append(script);
	}

	configuredMeasurementId = measurementId;
};

export const setGa4AnalyticsConsent = (
	measurementId: string | null,
	granted: boolean
): void => {
	analyticsAllowed =
		granted && measurementId !== null && isGa4MeasurementId(measurementId);

	if (analyticsAllowed && measurementId) {
		loadGa4(measurementId);
	}
};

export const trackGa4Event = (
	name: Ga4EventName,
	parameters: Ga4EventParameters = {},
	onComplete?: () => void
): void => {
	if (!analyticsAllowed || typeof window === "undefined" || !window.gtag) {
		onComplete?.();
		return;
	}
	const pageContext = {
		page_location: `${window.location.origin}/application`,
		page_referrer: `${window.location.origin}/`,
	};

	if (!onComplete) {
		window.gtag("event", name, { ...parameters, ...pageContext });
		return;
	}

	let completed = false;
	const finish = () => {
		if (completed) return;
		completed = true;
		onComplete();
	};

	window.setTimeout(finish, 1000);
	window.gtag("event", name, {
		...parameters,
		...pageContext,
		event_callback: finish,
		event_timeout: 1000,
	});
};
